/**
 * Auth-page routing for logged-out visitors.
 *
 * Every `auth: true` route sends a new device (`!hasUserAccount()` /
 * no soulwise-has-account) to SignupPage and a returning device to
 * LoginPage. Direct /login and /signup are not auth-gated and stay as
 * routed. The free-content session/query marker is only used for copy
 * and funnel params, not for choosing the auth page.
 */

import { stripLocaleFromPathname } from './locale';
import { hasUserAccount } from './returningUser';

export const FREE_CONTENT_AUTH_FLOW_STORAGE_KEY = 'soulwise-free-content-auth';
export const AUTH_RETURN_TO_STORAGE_KEY = 'soulwise-auth-return-to';
export const FREE_CONTENT_AUTH_QUERY_PARAM = 'sw_free';
export const RETURN_TO_QUERY_PARAM = 'from';

const canUseSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

/**
 * Mark that the current session is a free-content unlock (Get free access).
 * Used for signup intro copy and funnel `is_free_content_flow`, not routing.
 */
export const markFreeContentAuthFlow = () => {
  if (!canUseSessionStorage()) {
    return;
  }
  try {
    window.sessionStorage.setItem(FREE_CONTENT_AUTH_FLOW_STORAGE_KEY, '1');
  } catch {
    // ignore quota / private mode
  }
};

export const isFreeContentAuthFlowActive = () => {
  if (!canUseSessionStorage()) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(FREE_CONTENT_AUTH_FLOW_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Only allow same-origin relative return paths. Reject protocol-relative
 * URLs, absolute http(s) URLs, and anything that does not start with `/`.
 */
export const sanitizeReturnPath = value => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith('/')) {
    return null;
  }
  if (trimmed.startsWith('//')) {
    return null;
  }
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)) {
    return null;
  }
  if (trimmed.includes('://')) {
    return null;
  }
  return trimmed;
};

/**
 * Login / signup / confirm pages are not a valid "original page" to return to.
 * Listing slugs that merely contain those words (e.g. `/l/login-workshop/id`)
 * are left alone because the locale-stripped path still starts with `/l/`.
 */
export const isAuthPagePath = value => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const pathOnly = value.split('?')[0].split('#')[0];
  const stripped = stripLocaleFromPathname(pathOnly);
  return /^\/(login|signup|confirm)(\/|$)/.test(stripped);
};

export const persistAuthReturnTo = from => {
  const safe = sanitizeReturnPath(from);
  if (!canUseSessionStorage() || !safe || isAuthPagePath(safe)) {
    return;
  }
  try {
    window.sessionStorage.setItem(AUTH_RETURN_TO_STORAGE_KEY, safe);
  } catch {
    // ignore quota / private mode
  }
};

export const readPersistedAuthReturnTo = () => {
  if (!canUseSessionStorage()) {
    return null;
  }
  try {
    const stored = sanitizeReturnPath(window.sessionStorage.getItem(AUTH_RETURN_TO_STORAGE_KEY));
    if (!stored || isAuthPagePath(stored)) {
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};

/**
 * Prefer history state, then IdP cookie, then `?from=`, then sessionStorage.
 * Auth-page paths are treated as unknown so the webview handoff never
 * sends people back to /login or /signup.
 */
export const resolveAuthReturnTo = ({ locationState, search, authInfoFrom } = {}) => {
  const searchParams = new URLSearchParams(search || '');
  const candidates = [
    locationState?.from,
    authInfoFrom,
    searchParams.get(RETURN_TO_QUERY_PARAM),
    readPersistedAuthReturnTo(),
  ];
  for (const candidate of candidates) {
    const safe = sanitizeReturnPath(candidate);
    if (safe && !isAuthPagePath(safe)) {
      return safe;
    }
  }
  return null;
};

export const resolveIsFreeContentFlow = ({ locationState, search } = {}) => {
  const searchParams = new URLSearchParams(search || '');
  return !!(
    locationState?.isFreeContentFlow ||
    searchParams.get(FREE_CONTENT_AUTH_QUERY_PARAM) === '1' ||
    isFreeContentAuthFlowActive()
  );
};

export const buildAuthRedirectSearch = ({ from, isFreeContentFlow } = {}) => {
  const params = new URLSearchParams();
  const safeFrom = sanitizeReturnPath(from);
  if (safeFrom && !isAuthPagePath(safeFrom)) {
    params.set(RETURN_TO_QUERY_PARAM, safeFrom);
  }
  if (isFreeContentFlow) {
    params.set(FREE_CONTENT_AUTH_QUERY_PARAM, '1');
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Relative path the native browser should load when we are not wrapping
 * the handoff in an auth URL: original page, else home.
 */
export const resolveHandoffPath = ({ from, homePath = '/' } = {}) => {
  const safe = sanitizeReturnPath(from);
  if (safe && !isAuthPagePath(safe)) {
    return safe;
  }
  return homePath || '/';
};

/**
 * Listing checkout / get-access and other auth-gated actions should resume
 * after signup. Random marketing/CMS pages should not be wrapped in /signup.
 */
export const isResumableHandoffFrom = from => {
  const kind = classifyFromPage(from);
  return kind !== 'none' && kind !== 'other';
};

/**
 * Path to send the user to after login/signup (email or IdP). Prefers a
 * sanitized `from`; otherwise `defaultReturn`.
 */
export const postAuthRedirectPath = ({ from, defaultReturn = '/' } = {}) => {
  const safe = sanitizeReturnPath(from);
  if (safe && !isAuthPagePath(safe)) {
    return safe;
  }
  return defaultReturn || '/';
};

export const classifyFromPage = from => {
  if (!from || typeof from !== 'string') {
    return 'none';
  }
  if (from.includes('/content-library')) {
    return 'content_library';
  }
  if (from.includes('/checkout')) {
    return 'checkout';
  }
  if (from.includes('/inbox')) {
    return 'inbox';
  }
  if (from.includes('/messages')) {
    return 'messages';
  }
  if (from.includes('/saved-events')) {
    return 'saved_events';
  }
  if (from.includes('/account')) {
    return 'account';
  }
  if (from.includes('/dashboard')) {
    return 'dashboard';
  }
  if (from.includes('/profile-settings')) {
    return 'profile';
  }
  if (from.includes('/order/')) {
    return 'order';
  }
  if (from.includes('/sale/')) {
    return 'sale';
  }
  if (from.includes('/listings')) {
    return 'manage_listings';
  }
  if (from.includes('/l/new')) {
    return 'new_listing';
  }
  if (/(^|\/)l\//.test(from)) {
    return 'listing';
  }
  return 'other';
};

/**
 * Decide which named auth route a protected page should send a logged-out
 * visitor to. Applies to every auth-gated route.
 *
 * No stored account → SignupPage.
 * hasUserAccount → LoginPage.
 */
export const resolveAuthPage = ({ hasAccount } = {}) => {
  const returning = typeof hasAccount === 'boolean' ? hasAccount : hasUserAccount();
  return returning ? 'LoginPage' : 'SignupPage';
};

/**
 * @deprecated Use resolveAuthPage. Kept so older call sites keep compiling;
 * routing is no longer scoped to free-content.
 */
export const resolveFreeContentAuthPage = (opts = {}) => resolveAuthPage(opts);

/**
 * Build history.state for an auth redirect so AuthenticationPage can show
 * the right intro and send the user back after login/signup.
 */
export const buildAuthRedirectState = ({ from, isFreeContentFlow }) => ({
  from,
  ...(isFreeContentFlow ? { isFreeContentFlow: true } : {}),
});

/**
 * Persist return-path + free-content marker for an auth redirect so the
 * native-browser handoff can reconstruct the original page even when
 * history.state is dropped.
 */
export const rememberAuthRedirect = ({ from, isFreeContentFlow } = {}) => {
  persistAuthReturnTo(from);
  if (isFreeContentFlow) {
    markFreeContentAuthFlow();
  }
};
